import BookingAuthStep from '@/components/booking/BookingAuthStep';
import { useCompanyBySlug } from '@/hooks/queries/useCompanyBySlug';
import { useCreateWalkInQueue } from '@/hooks/queries/useCreateWalkInQueue';
import { useAuth } from '@/hooks/useAuth';
import { useBookingPhoneVerification } from '@/hooks/useBookingPhoneVerification';
import { usePhoneAuthFlow } from '@/hooks/usePhoneAuthFlow';
import { getApiErrorMessage } from '@/lib/api/error';
import { resolveCompanyAttendanceCapabilities } from '@/lib/company/capabilities';
import { canCompanyAcceptBookings } from '@/lib/company/status';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const WalkInPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, setVerificationToken, signIn, user } = useAuth();
  const { company } = useCompanyBySlug(slug);
  const companyCapabilities = resolveCompanyAttendanceCapabilities(company);
  const phoneAuthFlow = usePhoneAuthFlow({
    initialUserName: typeof user?.name === 'string' ? user.name : '',
    storageKey: slug ? `walk-in:${slug}` : undefined,
  });
  const { createWalkInQueue, isPending } = useCreateWalkInQueue();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedEntry, setConfirmedEntry] = useState<{
    createdAt?: string | null;
    name: string;
    phoneNumber: string;
    protocol?: string | null;
  } | null>(null);
  const hasBlockedUnavailableWalkIn = useRef(false);

  useEffect(() => {
    if (typeof user?.name === 'string' && user.name.trim().length > 0 && phoneAuthFlow.userName.trim().length === 0) {
      phoneAuthFlow.setUserName(user.name);
    }
  }, [phoneAuthFlow, user?.name]);

  useEffect(() => {
    if (!isAuthenticated || !user?.phoneNumber) {
      return;
    }

    if (phoneAuthFlow.phoneNumber.trim().length === 0) {
      phoneAuthFlow.setPhoneNumber(user.phoneNumber);
    }

    if (phoneAuthFlow.authStep === 'phone') {
      phoneAuthFlow.setAuthStep('name');
    }
  }, [isAuthenticated, phoneAuthFlow, user?.phoneNumber]);

  useEffect(() => {
    if (!company || hasBlockedUnavailableWalkIn.current) {
      return;
    }

    if (!canCompanyAcceptBookings(company)) {
      hasBlockedUnavailableWalkIn.current = true;

      toast('Este local ainda não está recebendo atendimento por aqui.', {
        description: 'Volte para a página do local e tente outro caminho.',
      });

      navigate(`/${company.slug}`, { replace: true });
      return;
    }

    if (!companyCapabilities.allowWalkInQueue) {
      hasBlockedUnavailableWalkIn.current = true;

      toast('Este local não está usando fila por aqui.', {
        description: 'Volte para a página do local e escolha outro caminho.',
      });

      navigate(`/${company.slug}`, { replace: true });
    }
  }, [company, companyCapabilities.allowWalkInQueue, navigate]);

  const submitWalkIn = async () => {
    if (!company?.id) {
      toast.error('Não foi possível entrar na fila', {
        description: 'Atualize a página e tente novamente.',
      });
      return false;
    }

    const customerName =
      phoneAuthFlow.userName.trim() || (typeof user?.name === 'string' ? user.name.trim() : '') || '';
    const customerPhoneNumber =
      phoneAuthFlow.phoneNumber.trim() || (typeof user?.phoneNumber === 'string' ? user.phoneNumber.trim() : '') || '';

    if (!customerName || !customerPhoneNumber) {
      toast.error('Não foi possível entrar na fila', {
        description: 'Confirme seu nome e telefone antes de continuar.',
      });
      return false;
    }

    try {
      const response = await createWalkInQueue({
        companyId: company.id,
        customerName,
        customerPhoneNumber,
      });
      setConfirmedEntry({
        createdAt: response.createdAt,
        name: customerName,
        phoneNumber: customerPhoneNumber,
        protocol: response.protocol,
      });
      phoneAuthFlow.reset();
      setIsConfirmed(true);
      return true;
    } catch (error) {
      toast.error('Não foi possível entrar na fila', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
      return false;
    }
  };

  const phoneVerification = useBookingPhoneVerification({
    onAuthStepChange: phoneAuthFlow.setAuthStep,
    onLinkedUserValidated: async () => {
      await submitWalkIn();
    },
    onVerificationTokenReceived: setVerificationToken,
    onVerificationSignIn: async (data) => {
      await signIn(data);
    },
    onVerificationCodeChange: phoneAuthFlow.setVerificationCode,
    phoneNumber: phoneAuthFlow.phoneNumber,
    verificationCode: phoneAuthFlow.verificationCode,
  });

  const title = useMemo(() => {
    if (isConfirmed) {
      return 'Fila confirmada';
    }

    return 'Entrar na fila';
  }, [isConfirmed]);

  const goBack = () => {
    if (isConfirmed) {
      navigate(`/${company?.slug ?? ''}`);
      return;
    }

    if (phoneAuthFlow.authStep === 'phone') {
      navigate(`/${company?.slug ?? ''}`);
      return;
    }

    phoneAuthFlow.setAuthStep('phone');
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
        <div className="container flex h-14 items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="hover:bg-muted -ml-2 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          >
            <ArrowLeft className="text-foreground h-5 w-5" />
          </button>
          <h1 className="text-foreground flex-1 text-center text-base font-semibold">{title}</h1>
          <div className="h-10 w-10 shrink-0" aria-hidden="true" />
        </div>
      </header>

      <div className="container mx-auto max-w-xl py-6">
        {isConfirmed ? (
          <div className="animate-scale-in py-8 text-center">
            <div className="bg-success/10 mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full">
              <CheckCircle2 className="text-success h-10 w-10" />
            </div>
            <h2 className="text-foreground mb-2 text-2xl font-bold">Você entrou na fila</h2>
            <p className="text-muted-foreground mb-8">
              {company?.name ? `Agora é só aguardar sua vez em ${company.name}.` : 'Agora é só aguardar sua vez.'}
            </p>

            <div className="border-border bg-card mx-auto mb-8 max-w-sm space-y-3 rounded-xl border p-5 text-left">
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Local</span>
                <span className="text-card-foreground text-sm font-medium">{company?.name ?? '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Nome</span>
                <span className="text-card-foreground text-sm font-medium">
                  {confirmedEntry?.name ?? 'Cadastro enviado'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Telefone</span>
                <span className="text-card-foreground text-sm font-medium">{confirmedEntry?.phoneNumber ?? '-'}</span>
              </div>
              {confirmedEntry?.createdAt ? (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground text-sm">Entrada</span>
                  <span className="text-card-foreground text-right text-sm font-medium">
                    {new Date(confirmedEntry.createdAt).toLocaleString('pt-BR')}
                  </span>
                </div>
              ) : null}
              {confirmedEntry?.protocol ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Protocolo</span>
                  <span className="text-card-foreground text-sm font-medium">{confirmedEntry.protocol}</span>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => navigate('/perfil?tab=bookings')}
                className="bg-primary text-primary-foreground h-12 rounded-xl px-8 font-semibold transition-all hover:opacity-90"
              >
                Acompanhar minha fila
              </button>
              <button
                type="button"
                onClick={() => navigate(`/${company?.slug ?? ''}`)}
                className="border-border text-foreground hover:bg-muted h-12 rounded-xl border px-8 font-semibold transition-colors"
              >
                Voltar ao local
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="border-border bg-card rounded-2xl border p-5">
              <p className="text-card-foreground text-sm font-medium">Fila sem agendar</p>
              <h2 className="text-foreground mt-2 text-2xl font-bold">{company?.name ?? 'Local'}</h2>
              <p className="text-muted-foreground mt-2 text-sm">
                Confirme seu telefone para entrar na fila e aguardar atendimento no local.
              </p>
            </div>

            <BookingAuthStep
              authStep={phoneAuthFlow.authStep}
              isRequestingCode={phoneVerification.isRequestingCode}
              isValidatingCode={phoneVerification.isValidatingCode}
              nameDescription="Seu nome para entrar na fila"
              onAuthStepChange={phoneAuthFlow.setAuthStep}
              onPhoneNumberChange={phoneAuthFlow.setPhoneNumber}
              onRequestCode={phoneVerification.requestCode}
              onUserNameChange={phoneAuthFlow.setUserName}
              onValidateCode={phoneVerification.validateCode}
              onVerificationCodeChange={phoneAuthFlow.setVerificationCode}
              phoneRequestMethod={phoneVerification.requestedMethod}
              phoneNumber={phoneAuthFlow.phoneNumber}
              userName={phoneAuthFlow.userName}
              verificationCode={phoneAuthFlow.verificationCode}
            />
          </div>
        )}
      </div>

      {!isConfirmed && phoneAuthFlow.authStep === 'name' && (
        <div className="sticky-bar">
          <div className="container mx-auto max-w-xl">
            <button
              type="button"
              onClick={() => void submitWalkIn()}
              disabled={phoneAuthFlow.userName.trim().length === 0 || isPending}
              className="bg-primary text-primary-foreground flex h-14 w-full items-center justify-center gap-2 rounded-xl text-base font-bold shadow-lg transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40"
            >
              {isPending ? 'Confirmando...' : 'Entrar na fila'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkInPage;
